import React from 'react';
import PropTypes from 'prop-types';

import timeUtils from '../../../../qti/utils/TimeUtils';
import CancelButton from './CancelButton';

import styles from './uploadInteraction.scss';
import a11yIIBScreenReaderService from '../../../../services/a11yIIBScreenReaderService';

class UploadViewFileUploading extends React.Component {
    static propTypes = {
        cancelUpload: PropTypes.func.isRequired,
        selectedFile: PropTypes.object.isRequired,
        services: PropTypes.object.isRequired,
        uploadProgress: PropTypes.number.isRequired,
        uploadProgressEstimatedMsRemaining: PropTypes.number,
    };

    constructor(props) {
        super(props);
        this.lastAnnouncedProgress = Math.ceil(props.uploadProgress * 100);
    }

    componentDidMount() {
        this.initiallyFocusedElement.focus();
    }

    componentDidUpdate(prevProps) {
        // Only process progress announcements if screen reader support is enabled
        if (a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported()) {
            const currentProgressPercentage = Math.ceil(this.props.uploadProgress * 100);
            const prevProgressPercentage = Math.ceil(prevProps.uploadProgress * 100);

            // Only update when progress actually changes and should be announced
            if (currentProgressPercentage !== prevProgressPercentage &&
                this.shouldAnnounceProgress(currentProgressPercentage)) {
                this.lastAnnouncedProgress = currentProgressPercentage;
            }
        }
    }

    /**
     * Determines if progress should be announced to screen readers.
     * Only announces every 30% to avoid overwhelming screen readers with frequent updates.
     * @param {number} currentProgress Current progress percentage (0-100)
     * @returns {boolean} True if progress should be announced
     */
    shouldAnnounceProgress = (currentProgress) => {
        // Guard against cases where accessibility is disabled
        if (!a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported()) {
            return false;
        }

        const progressIncrement = 30;
        // Always announce 100% completion
        if (currentProgress >= 100) {
            return true;
        }
        return currentProgress - this.lastAnnouncedProgress >= progressIncrement;
    };

    /**
     * Gets the text for the estimated time left message, based on the number of milliseconds left before completion.
     * @param {int} ms Estimated number of milliseconds remaining.
     */
    getTimeLeftString = (ms) => {
        const { t } = this.props.services.translationService;
        const formattedDuration = timeUtils.formatDuration(ms, t);

        return t('interactions.upload.estimatedTimeLeft', { formattedDuration });
    };

    render() {
        const { uploadProgress, uploadProgressEstimatedMsRemaining, selectedFile, services } = this.props;
        const { t } = services.translationService;
        const formattedDuration = timeUtils.formatDuration(uploadProgressEstimatedMsRemaining, t);
        const progressPercentage = Math.ceil(uploadProgress * 100);
        const tabIndexValue = a11yIIBScreenReaderService.getTabIndexValue(undefined);
        const ariaLabelValue = a11yIIBScreenReaderService.getAriaLabelValue(
            undefined,
            t('interactions.upload.a11yUploadingFileName', { fileName: selectedFile.name }),
        );

        const a11yIterationForIIBScreenReaderSupported = a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported();
        const shouldAnnounceProgress = a11yIterationForIIBScreenReaderSupported && this.shouldAnnounceProgress(progressPercentage);
        const ariaHiddenValue = a11yIterationForIIBScreenReaderSupported ? true : undefined;

        return (
            <div className={styles.fileUploadingView}>
                <div>
                    <span
                        ref={(span) => { this.initiallyFocusedElement = span; }}
                        tabIndex="-1"
                        className={styles.fileUploadingWrapper}
                        dangerouslySetInnerHTML={{
                            __html: t('interactions.upload.uploadingFileName', { fileName: selectedFile.name }),
                        }}
                    />
                    <div className={styles.progressBarRow}>
                        <progress
                            max="100"
                            value={progressPercentage}
                            ref={(progressBar) => { this.progressBar = progressBar; }}
                            tabIndex={tabIndexValue}
                            aria-label={ariaLabelValue}
                        >
                            <div className={styles.progressBarWrapper} aria-hidden={ariaHiddenValue}>
                                <div className={styles.progressBar} style={{ width: `${progressPercentage}%` }}>&nbsp;</div>
                            </div>
                        </progress>
                        <CancelButton
                            services={services}
                            cancelUpload={this.props.cancelUpload}
                        />
                        {a11yIterationForIIBScreenReaderSupported && (
                            <div
                                aria-live="polite"
                                aria-atomic="true"
                                className="sr-only"
                            >
                                {shouldAnnounceProgress ? `${progressPercentage}%` : ''}
                            </div>
                        )}
                    </div>

                    <div className={styles.progressInfoRow}>
                        <div>{progressPercentage}%</div>
                        <div
                            dangerouslySetInnerHTML={{
                                __html: t('interactions.upload.estimatedTimeLeft', { formattedDuration }),
                            }}
                        />
                    </div>
                </div>
            </div>
        );
    }
}

export default UploadViewFileUploading;
